'use client'

// imports
import { useState, useEffect } from 'react';
import Link from 'next/link';

// Constants
import { DONATE_FALLBACK_URL } from '@/lib/constants';

// lib files
import { getPassportLearnMoreUrl } from '@/lib/helpers/get-passport-learn-more-url';
import { useIdentity } from '@/lib/hooks/useIdentity';
import { VideoTypeEnum, RelatedVideoAsset } from '@/lib/types/api/video';
import { LinkWithUrlAndText } from '@/lib/types/api/link-with-url-and-text';

// components
import DonateLinkButton from '@/components/Button/DonateLinkButton';
import ITSImage from '@/components/ITSImage/ITSImage';
import LinkButton from '@/components/Button/LinkButton';
import KabobMenu from '@/components/KabobMenu/KabobMenu';
import MezzanineVideoInitializer from '@/components/MezzanineVideoInitializer/MezzanineVideoInitializer';
import MyListButton from '@/components/Button/MyList/MyListButton';
import { StationData } from '@/lib/types/api/stations-data';
import VideoPlayer from  '@/components/VideoPlayer/VideoPlayer';

// svgs
import CompassRose from '@/public/svg/compass-rose.svg';
import PlayIcon from "@/public/svg/play.svg";
import ArrowRightIcon from '@/public/svg/arrow-right.svg';

// styles
import styles from './VideoHero.module.scss';

interface VideoHeroProps {
  depIsPassportMember?: boolean;
  host: string;
  isSVP: boolean;
  stationData?: StationData;
  targetVideoCid: string;
  targetVideoDescription?: string;
  targetVideoImageToUse?: string;
  targetVideoIsPassport?: boolean;
  targetVideoLegacyTpMediaId?: number;
  targetVideoLinks?: Array<LinkWithUrlAndText>;
  targetVideoRelatedAsset?: RelatedVideoAsset | null;
  targetVideoSlug: string;
  targetVideoTitle: string;
  targetVideoType?: VideoTypeEnum;
  targetVideoShowSlug?: string;
  targetVideoShowTitle?: string;
  targetVideoShowLogo?: string;
}

/**
 * <VideoHero /> does two things - it directs users to a promoted target video,
 * but also allows an inline video playback of a related asset.
 * @param {VideoHeroProps}
 * @returns {React.FC}
*/
const VideoHero: React.FC<VideoHeroProps> = (props) => {
  const {
    depIsPassportMember,
    host,
    isSVP,
    stationData,
    targetVideoCid,
    targetVideoDescription,
    targetVideoImageToUse,
    targetVideoIsPassport = false,
    targetVideoLegacyTpMediaId,
    targetVideoLinks,
    targetVideoRelatedAsset,
    targetVideoSlug,
    targetVideoTitle,
    targetVideoType,
    targetVideoShowSlug,
    targetVideoShowTitle,
    targetVideoShowLogo,
  } = props;
  const [showVideo, setShowVideo] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const { cachedIdentity } = useIdentity();

  useEffect(() => {
    setIsClient(true);
  }, []);

  const hasRelatedAsset = targetVideoRelatedAsset ? true : false;

  // Filter out links with empty url or text
  const validLinks = targetVideoLinks?.filter(link => link.url && link.text) || [];

  if(!stationData) {
    var short_common_name = 'PBS'
    var passport_url = ''
  } else {
    var { attributes: station_info } = stationData;
    var {
      short_common_name,
      passport_url,
    } = station_info;
  }

  const donateUrl = passport_url || DONATE_FALLBACK_URL;
  const passportLearnMoreUrl = getPassportLearnMoreUrl(stationData);
  let passportCalloutText: string | null = `Watch this video with ${short_common_name} Passport`;
  let isDonateAndStartWatchingCTA: boolean = false;

  // Only update callout text on client-side to prevent hydration mismatch
  if (isClient) {
    // Client-side will use actual profile data after hydration
    const isPassportMember = depIsPassportMember || cachedIdentity?.isPassport || false;
    const isPassportMemberOfLocalizedStation =
      stationData ? (cachedIdentity?.isPassportMemberOf(stationData) ?? false) : false;

    switch(true) {
      case isPassportMemberOfLocalizedStation:
        passportCalloutText = `Available with your ${short_common_name} Passport benefit`;
        break;
      case isPassportMember:
        passportCalloutText = `Available with your PBS Passport benefit`;
        break;
    }

    // Show a Donate CTA when the user is not a Passport Member and it is a Passport Video
    if (!isPassportMember && targetVideoIsPassport) {
      isDonateAndStartWatchingCTA = true;
    }
  }

  const videoDescription = targetVideoDescription || '';

  const ShowLogoOrTitle = () => {
    if (!targetVideoShowSlug || !targetVideoShowTitle) {
      return null;
    }

    return (
      <>
        { targetVideoShowLogo ? (
          <h2 className={styles.video_show_logo}>
            <Link href={`/show/${targetVideoShowSlug}/`}>
              <ITSImage
                src={targetVideoShowLogo}
                alt={targetVideoShowTitle}
                width={240}
              />
            </Link>
          </h2>
        ) : (
          <h2 className={styles.video_show_overtitle}>
            <Link href={`/show/${targetVideoShowSlug}/`}>
              {targetVideoShowTitle}
            </Link>
          </h2>
        )}
      </>
    )
  }

  const VideoHeroKabobMenuItems = () => (
    <ul>
      <li>
        <MyListButton
          style='kabobMenu'
          video={{
            cid: targetVideoCid,
            slug: targetVideoSlug,
            title: targetVideoTitle,
            legacy_tp_media_id: targetVideoLegacyTpMediaId,
            video_type: targetVideoType,
            show: targetVideoShowTitle ? {
              title: targetVideoShowTitle,
            } : undefined,
          }}
        />
      </li>
      { targetVideoShowSlug && (
        <li className={styles.video_hero_kabob_item_explore_the_show}>
          <Link href={`/show/${targetVideoShowSlug}/`}>
            <PlayIcon />
            <span>Explore the Show</span>
          </Link>
        </li>
      )}
     {isDonateAndStartWatchingCTA && (
        <li className={styles.video_hero_kabob_item_learn_more}>
          <CompassRose />
          <Link href={passportLearnMoreUrl} >
            What is Passport?
          </Link>
        </li>
      )}
      {validLinks.map((link, index) => (
        <li className={styles.video_hero_kabob_item_link} key={index}>
          <Link href={link.url} target="_blank">
            <ArrowRightIcon />
            <span>{link.text}</span>
          </Link>
        </li>
      ))}
    </ul>
  )

  const VideoHeroPassportCallout = () => (
    <p className={styles.video_passport_callout}>
      <CompassRose className={styles.compass_rose_icon}/>
      {passportCalloutText}
    </p>
  )

  const VideoHeroPrimaryCTAButton = () => (
    isDonateAndStartWatchingCTA ? (
      <DonateLinkButton
        href={donateUrl}
        style={'light_blue'}
        gtmEventData={{
          feature_category: "video hero",
          feature_name: "passport video hero donate link (non-passport member)",
          object_text: "Donate & Start Watching",
          object_url: donateUrl,
        }}
      >
        Donate & Start Watching
      </DonateLinkButton>
    ) : (
      <LinkButton
        href={`/video/${targetVideoSlug}/`}
        style="white"
        iconBefore="play"
      >
        Watch Now
      </LinkButton>
  ))

  const VideoHeroOverlay = (props: { isLoaded: boolean}) => {
    const { isLoaded } = props;
    return (
      <div className={isLoaded ? styles.video_hero_loaded_overlay : styles.video_hero_initial_overlay}>
        <ShowLogoOrTitle />
        <h3 className={styles.video_title}>
          <Link href={`/video/${targetVideoSlug}/`}>
            {targetVideoTitle}
          </Link>
        </h3>
        <p className={styles.video_description}>
          {videoDescription}
        </p>
        {targetVideoIsPassport && stationData && <VideoHeroPassportCallout />}
        <div className={isLoaded ? styles.video_hero_loaded_overlay_buttons : styles.video_hero_initial_overlay_buttons}>
          <VideoHeroPrimaryCTAButton />
          { targetVideoShowSlug && (
            <LinkButton
              href={`/show/${targetVideoShowSlug}/`}
              style="white_ghost"
              className={styles.video_hero_explore_the_show_button}
              >
              Explore the Show
            </LinkButton>
          )}

          <KabobMenu
            className={styles.video_hero_kabob_button_above_sm}
            contentClassName={styles.video_hero_kabob_content_above_sm}
          >
            <VideoHeroKabobMenuItems />
          </KabobMenu>
        </div>
        <KabobMenu
          className={styles.video_hero_kabob_button_below_sm}
          contentClassName={styles.video_hero_kabob_content_below_sm}
        >
          <VideoHeroKabobMenuItems />
        </KabobMenu>
      </div>
    )
  }

  const InitialVideoHero = () => {
    return (
      <div className={styles.video_hero_initial}>
        <MezzanineVideoInitializer
          videoType={targetVideoRelatedAsset?.video_type || VideoTypeEnum.Video }
          imgSrc={targetVideoImageToUse || null }
          alt={targetVideoTitle}
          showWatchButton={hasRelatedAsset}
          onClick={() => setShowVideo(true)}
          verticalOffsetButton={true}
          width={1700}
          className={styles.video_hero_video_initializer}
        />
        <VideoHeroOverlay isLoaded={false} />
      </div>
  )};

  const VideoPlayerLoaded = () => {
    const playerConfig = {
      autoplay: true,
      embedType: isSVP ? 'stationplayer' : 'portalplayer',
      disableContinuousPlay: true,
      host,
      previewLayout: true,
      stationId: stationData?.id,
      callsign: stationData?.attributes.call_sign,
    }

    return (
      <div className={styles.video_hero_loaded}>
        <VideoPlayer
          // This non-null assertion is safe because the VideoPlayer
          // is only mounted in cases when there *is* a related assset.
          video={targetVideoRelatedAsset!}
          playerConfig={playerConfig}
          className={styles.video_hero_loaded_video_player}
        />

        <VideoHeroOverlay isLoaded={true} />
      </div>
    )
  };

  return (
    showVideo && hasRelatedAsset ?
      (<section>
        <VideoPlayerLoaded />
      </section>) :
      (<section>
        <InitialVideoHero />
      </section>)
  );
}

export default VideoHero;
